import React, { FunctionComponent } from "react";
import { ReactNode } from "react";
import { ContentPage } from "../../../../types/customization/content";
import { Template, TemplateDateCellDefinition } from "../../../../types/customization/template";
import { sizeDmmToMm } from "../../../../utils/customization/box";
import {
  findCellColorDefinition,
  findColorDefinition,
} from "../../../../utils/customization/finders";
import Box from "../../../atoms/customization/Box";
import DateCellForcedText from "../DateCellForcedText";
import ForcedImage from "../ForcedImage";
import "./style.scss";

interface Props {
  elementDefinition: TemplateDateCellDefinition;
  page: ContentPage;
  ppmm: number;
  template: Template;
}

const DateCell: FunctionComponent<Props> = ({ elementDefinition, page, ppmm, template }) => {
  const colorDefinition = findColorDefinition(page.backgroundColorKey, template.colorDefinitions);
  const dateCellContent = page.dateCells?.find(
    (dateCell) => dateCell.dateCellKey === elementDefinition.key
  );
  if (!dateCellContent || !colorDefinition) {
    return null;
  }
  const cellColorDefinition = findCellColorDefinition(
    dateCellContent.cellColor.key,
    colorDefinition
  );
  let caseColorDefinition = cellColorDefinition?.active;
  if (dateCellContent.cellColor.state === "inactive") {
    caseColorDefinition = cellColorDefinition?.inactive;
  }

  const style: React.CSSProperties = {
    backgroundColor: caseColorDefinition?.backgroundValue,
  };

  const elements = elementDefinition.elements.map((cellElementDefinition) => {
    let element: ReactNode = null;
    switch (cellElementDefinition.type) {
      case "forced-image": {
        element = (
          <ForcedImage elementDefinition={cellElementDefinition} ppmm={ppmm} template={template} />
        );
        break;
      }
      case "forced-text": {
        element = (
          <DateCellForcedText
            cellColorDefinition={caseColorDefinition}
            dateCellContent={dateCellContent}
            elementDefinition={cellElementDefinition}
            ppmm={ppmm}
            template={template}
          />
        );
        break;
      }
      default: {
        const exhaustiveCheck: never = cellElementDefinition;
        throw new Error(`Unsupported type ${(exhaustiveCheck as { type: string }).type}`);
      }
    }

    const elementSizeMm = sizeDmmToMm(elementDefinition);

    return (
      <Box
        elementDimensions={{
          width: cellElementDefinition.width,
          height: cellElementDefinition.height,
          x: cellElementDefinition.x,
          y: cellElementDefinition.y,
          anchorSelf: cellElementDefinition.anchorSelf,
          anchorPage: cellElementDefinition.anchorElement,
        }}
        forced={true}
        key={`DateCell-${cellElementDefinition.type}-${cellElementDefinition.key}`}
        pageDimensions={elementSizeMm}
        ppmm={ppmm}
        zIndex={cellElementDefinition.zIndex}
      >
        {element}
      </Box>
    );
  });

  return (
    <div className="DateCell" style={style}>
      {elements}
    </div>
  );
};

export default DateCell;
